import { createSlice } from "@reduxjs/toolkit";
import {
  performCreditCheck,
  subscribeToNewsletter,
} from "../thunks/customer.thunks";

export enum CREDIT_CHECK_REASON {
  DENIED_PREVS_CHECK = "DENIED_PREVS_CHECK",
  DENIED_LOW_CREDIT = "DENIED_LOW_CREDIT",
  BYPASSED_PREVS_CHECK = "BYPASSED_PREVS_CHECK",
  BYPASSED_OK_CREDIT = "BYPASSED_OK_CREDIT",
}

type LoadingState = "idle" | "pending" | "fulfilled" | "rejected";

interface CustomerState {
  creditCheck: {
    loading: LoadingState;
    error: string | null;
    denied: boolean;
    reason: CREDIT_CHECK_REASON | null;
  };
  newsletter: {
    loading: LoadingState;
    error: string | null;
    subscribed: boolean;
  };
}

const initialState: CustomerState = {
  creditCheck: {
    loading: "idle",
    error: null,
    denied: false,
    reason: null,
  },
  newsletter: {
    loading: "idle",
    error: null,
    subscribed: false,
  },
};

const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    resetCustomerState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(performCreditCheck.pending, (state) => {
        state.creditCheck.loading = "pending";
        state.creditCheck.error = null;
        state.creditCheck.denied = false;
        state.creditCheck.reason = null;
      })
      .addCase(performCreditCheck.fulfilled, (state, action) => {
        state.creditCheck.loading = "fulfilled";
        state.creditCheck.denied = action.payload.denied;
        state.creditCheck.reason = action.payload.reason;
      })
      .addCase(performCreditCheck.rejected, (state, action) => {
        state.creditCheck.loading = "rejected";
        state.creditCheck.error = action.error.message || "Credit check failed";
        state.creditCheck.denied = true;
      })
      .addCase(subscribeToNewsletter.pending, (state) => {
        state.newsletter.loading = "pending";
        state.newsletter.error = null;
      })
      .addCase(subscribeToNewsletter.fulfilled, (state, action) => {
        state.newsletter.loading = "fulfilled";
        state.newsletter.subscribed = action.payload.subscribe;
      })
      .addCase(subscribeToNewsletter.rejected, (state, action) => {
        state.newsletter.loading = "rejected";
        state.newsletter.error =
          action.error.message || "Failed to handle newsletter subscription";
      });
  },
});

export const { resetCustomerState } = customerSlice.actions;
export default customerSlice.reducer;

// Selectors
export const selectCustomerState = (state: { customer: CustomerState }) =>
  state.customer;
export const selectCreditCheckStatus = (state: { customer: CustomerState }) =>
  state.customer.creditCheck;
